import React, { useState, useMemo } from "react";
import { T, AWARD_COLORS, fmt } from "./theme";
import { SectionHeader, Card, Avatar, Input, AwardBadge, Btn } from "./ui";
import { Icon } from "./Icons";
import { TIMES } from "./data";
import { calcPontosCorretor, calcAwardAtual } from "./awards";
import { supabase, isSupabaseReady } from "./supabase";

// ═══════════════════════════════════════════════════════════════════════
// CORRETORES — admin: lista + ativar/desativar pendentes
// ═══════════════════════════════════════════════════════════════════════

export default function Corretores({ corretores, setCorretores, currentUser, onSelect }) {
  const [busca, setBusca] = useState("");
  const [filtroUf, setFiltroUf] = useState("todos");
  const [filtroStatus, setFiltroStatus] = useState("todos"); // todos | ativos | pendentes
  const [loadingId, setLoadingId] = useState(null);

  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "admin_master" || currentUser?.role === "proprietario" || currentUser?.role === "comercial";
  const ufs = [...new Set(corretores.map(c => c.uf).filter(Boolean))];
  const pendentes = corretores.filter(c => c.ativo === false && !c.banido).length;
  const banidos = corretores.filter(c => c.banido).length;

  const lista = useMemo(() => {
    let r = corretores;
    // Banidos: só aparecem no filtro "banidos". Todos os outros filtros ocultam.
    if (filtroStatus === "banidos")   r = r.filter(c => c.banido);
    else if (filtroStatus === "ativos")    r = r.filter(c => c.ativo !== false && !c.banido);
    else if (filtroStatus === "pendentes") r = r.filter(c => c.ativo === false && !c.banido);
    else                              r = r.filter(c => !c.banido); // "todos" oculta banidos
    if (filtroUf !== "todos") r = r.filter(c => c.uf === filtroUf);
    if (busca) r = r.filter(c => c.nome.toLowerCase().includes(busca.toLowerCase()));
    return r.map(c => {
      const p = calcPontosCorretor(c);
      const a = calcAwardAtual(p.total);
      return { ...c, pontos: p.total, award: a };
    }).sort((a, b) => {
      if ((a.ativo === false) !== (b.ativo === false)) return a.ativo === false ? -1 : 1;
      return b.pontos - a.pontos;
    });
  }, [corretores, busca, filtroUf, filtroStatus]);

  async function alterarAtivo(corretor, novoEstado) {
    if (!isSupabaseReady || !corretor?._uuid && !corretor?.id) return;
    const id = corretor._uuid || corretor.id;
    const acao = novoEstado ? "ativar" : "desativar";
    if (!window.confirm(`Confirmar ${acao} ${corretor.nome}?`)) return;
    setLoadingId(id);
    try {
      const { error } = await supabase.from("corretores").update({ ativo: novoEstado }).eq("id", id);
      if (error) throw error;
      if (setCorretores) {
        setCorretores(prev => prev.map(c => (c._uuid === id || c.id === id) ? { ...c, ativo: novoEstado } : c));
      }
    } catch (e) {
      alert("Erro: " + e.message);
    } finally {
      setLoadingId(null);
    }
  }

  async function banir(corretor, novoEstado) {
    if (!isSupabaseReady || !corretor?._uuid && !corretor?.id) return;
    const id = corretor._uuid || corretor.id;
    const msg = novoEstado
      ? `Banir ${corretor.nome}? Ele some da rede, ranking e listas — mas o histórico de vendas e comissões fica preservado.`
      : `Reativar ${corretor.nome}? Ele volta a aparecer normalmente.`;
    if (!window.confirm(msg)) return;
    setLoadingId(id);
    try {
      const payload = novoEstado ? { banido: true, ativo: false } : { banido: false, ativo: true };
      const { error } = await supabase.from("corretores").update(payload).eq("id", id);
      if (error) throw error;
      if (setCorretores) {
        setCorretores(prev => prev.map(c => (c._uuid === id || c.id === id) ? { ...c, ...payload } : c));
      }
    } catch (e) {
      alert("Erro: " + e.message);
    } finally {
      setLoadingId(null);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Corretores"
        subtitle={`${corretores.length} corretores cadastrados${pendentes ? ` · ${pendentes} pendente(s)` : ""}`}
        icon={<Icon.users size={24} />}
        right={isAdmin && (
          <Btn onClick={() => window.open("/altaperformance?ref=gilzao", "_blank")} icon={<Icon.plus size={14} />}>
            Cadastrar
          </Btn>
        )}
      />

      {isAdmin && pendentes > 0 && filtroStatus !== "pendentes" && (
        <button onClick={() => setFiltroStatus("pendentes")} style={{
          padding: "10px 14px", borderRadius: T.r.md,
          background: T.warn + "15", border: `1px solid ${T.warn}44`,
          color: T.warn, fontSize: 12, fontWeight: 600,
          cursor: "pointer", textAlign: "left", fontFamily: "inherit",
        }}>
          ⚠️ {pendentes} cadastro(s) aguardando aprovação — clique pra ver
        </button>
      )}

      {/* Filtros */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Input placeholder="Buscar corretor..." value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        {isAdmin && (
          <div style={{ display: "flex", gap: 6 }}>
            {[
              { id: "todos",     label: "Todos" },
              { id: "ativos",    label: "Ativos" },
              { id: "pendentes", label: `Pendentes${pendentes ? ` (${pendentes})` : ""}` },
              { id: "banidos",   label: `Banidos${banidos ? ` (${banidos})` : ""}` },
            ].map(f => (
              <button key={f.id} onClick={() => setFiltroStatus(f.id)} style={{
                background: filtroStatus === f.id ? T.brand : "transparent",
                color: filtroStatus === f.id ? T.textInv : T.textSub,
                border: `1px solid ${filtroStatus === f.id ? T.brand : T.borderMid}`,
                borderRadius: T.r.md, padding: "8px 14px",
                fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}>{f.label}</button>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["todos", ...ufs].map(uf => (
            <button key={uf} onClick={() => setFiltroUf(uf)} style={{
              background: filtroUf === uf ? T.brand : "transparent",
              color: filtroUf === uf ? T.textInv : T.textSub,
              border: `1px solid ${filtroUf === uf ? T.brand : T.borderMid}`,
              borderRadius: T.r.md, padding: "6px 10px",
              fontSize: 11, fontWeight: 500, cursor: "pointer",
              textTransform: "uppercase", letterSpacing: 0.5,
            }}>{uf === "todos" ? "Todas UFs" : uf}</button>
          ))}
        </div>
      </div>

      {/* Lista */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {lista.length === 0 && (
          <div style={{ textAlign: "center", padding: 30, color: T.textDim }}>Nenhum corretor encontrado.</div>
        )}
        {lista.map((c, i) => {
          const color = AWARD_COLORS[c.award.cor];
          const time = TIMES.find(t => t.id === c.timeId);
          const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
          const pendente = c.ativo === false && !c.banido;
          const banido = !!c.banido;
          const id = c._uuid || c.id;
          const borderColor = banido ? T.err : pendente ? T.warn : "transparent";
          return (
            <Card key={c.id || c._uuid} style={{
              padding: 18,
              borderLeft: `3px solid ${borderColor}`,
              opacity: banido ? 0.6 : (pendente ? 0.95 : 1),
            }}>
              <div style={{ display: "grid", gridTemplateColumns: "40px 48px 1fr auto auto", gap: 16, alignItems: "center" }}>
                <div className="serif" style={{ fontSize: 18, color: T.textDim, fontWeight: 600, textAlign: "center" }}>
                  {(pendente || banido) ? "—" : `#${i + 1}`}
                </div>
                <Avatar initials={initials} size={44} tone={color.fg} photo={c.foto || c.foto_url} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                      onClick={() => onSelect && onSelect(c)}
                      style={{
                        background: "none", border: "none", padding: 0, cursor: onSelect ? "pointer" : "default",
                        color: T.brand, fontSize: 16, fontWeight: 600,
                        fontFamily: "'Fraunces', Georgia, serif", letterSpacing: -0.2,
                        textAlign: "left", textDecoration: onSelect ? "underline" : "none", textUnderlineOffset: 3,
                        textDecorationColor: T.brand + "44",
                      }}
                    >{c.nome}</button>
                    {banido && (
                      <span style={{
                        padding: "2px 8px", borderRadius: 100,
                        background: T.err + "22", border: `1px solid ${T.err}66`,
                        fontSize: 9, color: T.err, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase",
                      }}>⛔ Banido</span>
                    )}
                    {pendente && (
                      <span style={{
                        padding: "2px 8px", borderRadius: 100,
                        background: T.warn + "22", border: `1px solid ${T.warn}44`,
                        fontSize: 9, color: T.warn, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase",
                      }}>Pendente</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: T.textSub, display: "flex", gap: 10, flexWrap: "wrap", marginTop: 3 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <Icon.pin size={10} /> {c.cidade}-{c.uf}
                    </span>
                    {time && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <Icon.team size={10} /> {time.nome}
                    </span>}
                    {c.telefone && <span>📞 {c.telefone}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: T.textDim, marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {!pendente && <>
                      <span>{c.vendasDiretas || 0} vendas</span>
                      <span>· {fmt.brl(c.vendasValor || 0)}</span>
                      <span>· {c.cadastrados || 0} cadastrados ({c.ativados || 0} ativados)</span>
                    </>}
                    {pendente && c.email && <span>✉️ {c.email}</span>}
                  </div>
                </div>
                {c.tipo === "proprietario" ? (
                  <span style={{
                    background: "#1a140f", color: "#d7be7c",
                    border: "1px solid #d7be7c66", borderRadius: 100,
                    padding: "3px 10px", fontSize: 10, fontWeight: 700,
                    letterSpacing: 1, textTransform: "uppercase",
                    fontFamily: "'Fraunces', Georgia, serif", whiteSpace: "nowrap",
                  }}>🦁 Founder</span>
                ) : (
                  <AwardBadge awardId={c.award.id} size="sm" />
                )}
                <div style={{ textAlign: "right", minWidth: 60 }}>
                  {!pendente ? (
                    <>
                      <div className="serif" style={{ fontSize: 22, fontWeight: 600, color: color.fg, lineHeight: 1 }}>{c.pontos}</div>
                      <div style={{ fontSize: 9, color: T.textDim, letterSpacing: 0.5, textTransform: "uppercase" }}>pts</div>
                    </>
                  ) : (
                    isAdmin && (
                      <Btn onClick={() => alterarAtivo(c, true)} disabled={loadingId === id} style={{ fontSize: 11 }}>
                        {loadingId === id ? "…" : "✅ Ativar"}
                      </Btn>
                    )
                  )}
                </div>
              </div>
              {isAdmin && !c.banido && (
                <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end", gap: 12 }}>
                  <button
                    onClick={() => banir(c, true)}
                    disabled={loadingId === id}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: T.err, fontSize: 10, fontFamily: "inherit",
                      textDecoration: "underline",
                    }}
                  >⛔ Banir corretor</button>
                </div>
              )}
              {isAdmin && c.banido && (
                <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end", gap: 12 }}>
                  <button
                    onClick={() => banir(c, false)}
                    disabled={loadingId === id}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: T.ok, fontSize: 10, fontFamily: "inherit",
                      textDecoration: "underline",
                    }}
                  >♻️ Reativar corretor</button>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
